import { Injectable } from '@angular/core';
import { Observable, map, finalize, Subject, shareReplay, tap, forkJoin, of, catchError, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../_services/config.service';
import { UserInfo } from '../../_models/UserInfo.model';
import { Label } from '../../_models/Label.model';
import { SidebarMenu } from 'src/app/_models/menu/SidebarMenu.model';
import { SpinnerService } from 'src/app/_services/SpinnerService';
import { BehaviorSubject } from 'rxjs';
import { Alias } from '../../_models/portal/Alias.model';
import { Portal } from 'src/app/_models/portal/Portal.model';
import { Role } from 'src/app/_models/portal/Role.model';
import { Page } from 'src/app/_models/portal/Page.model';
import { CompanyMenuOrgStore, MenuStileSottomenu, activeMenuOrgHidden, activeMenuOrgNodes, applyMenuOrganization, collectMenuOrgShortcutKeys, parseMenuOrgStore } from 'src/app/_services/menu-organization';
import { buildShortcutLeaves, modulesForShortcutKeys } from 'src/app/_services/company-menu-shortcuts';
import { RfiModeService } from 'src/app/_services/rfi-mode.service';

@Injectable({
	providedIn: 'root',
})
export class PortalService {
	authorizated = false;
	public token: string = '';
	reload = new Subject<boolean>();
	private sceltaportaleSubject = new BehaviorSubject<boolean>(false);
	sceltaportale$ = this.sceltaportaleSubject.asObservable();

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private spinnerService: SpinnerService,
		private rfiMode: RfiModeService,
	) {}

	set_reload(r: any) {
		this.reload.next(r);
	}

	activateSceltaPortale() {
		this.sceltaportaleSubject.next(true);
	}

	deactivateSceltaPortale() {
		this.sceltaportaleSubject.next(false);
	}

	// Menu — cache per-load condivisa tra sidebar, topnav e PageAccessGuard: il menu non
	// cambia durante la sessione (il cambio azienda/portale passa da un reload completo,
	// il salvataggio dell'organizzazione menu passa da invalidateMenuCache + menuChanged$)
	private menuItemsCache: { key: string; obs: Observable<any>; store$: Observable<CompanyMenuOrgStore | null> } | null = null;
	menuChanged$ = new Subject<void>();

	invalidateMenuCache() {
		this.menuItemsCache = null;
	}

	getMenuItems(prt_id: number, cmp_id: number): Observable<any> {
		const key = `${prt_id}:${cmp_id}`;
		if (this.menuItemsCache && this.menuItemsCache.key === key) {
			return this.menuItemsCache.obs;
		}
		this.spinnerService.show();
		// menu risolto dai preset + organizzazione per azienda (cmp_menu_org): la
		// riorganizzazione è presentazionale, l'insieme di voci/URL resta invariato
		const menu$ = this.http
			.get(`${this.configService.portalUrl}menu/sidebarmenu/${prt_id}/${cmp_id}`, {
				observe: 'response',
			})
			.pipe(map((response) => response.body));
		const store$ = this.http.get<any>(`${this.configService.companiesUrl}companies/menuorg?cmp_id=${cmp_id}`).pipe(
			map((res) => parseMenuOrgStore(res?.cmp_menu_org)),
			catchError(() => of(null)),
			shareReplay(1),
		);
		const org$ = store$.pipe(map((store) => ({ nodes: activeMenuOrgNodes(store), nascoste: activeMenuOrgHidden(store) })));
		const obs = forkJoin([menu$, org$]).pipe(
			switchMap(([menu, { nodes, nascoste }]) => {
				// Scorciatoie sezioni hub nel profilo attivo: servono le funzioni dei
				// moduli coinvolti per filtrarle per ruolo (stessa visibilità delle card
				// delle pagine hub); senza scorciatoie nessuna chiamata extra.
				const keys = collectMenuOrgShortcutKeys(nodes);
				if (!keys.length) {
					return of(applyMenuOrganization((menu as any) || [], nodes, null, nascoste));
				}
				const moduli = modulesForShortcutKeys(keys);
				const funzioni$ = moduli.length ? forkJoin(moduli.map((m) => this.getFunzioniByUser(m).pipe(catchError(() => of([]))))) : of([] as any[]);
				return forkJoin([funzioni$, this.rfiMode.attivo(cmp_id)]).pipe(
					map(([liste, rfi]: [any[], boolean]) => {
						const byModule = new Map<string, any[]>(moduli.map((m, i) => [m, liste[i] || []]));
						return applyMenuOrganization((menu as any) || [], nodes, buildShortcutLeaves(byModule, cmp_id, rfi, (menu as any) || []), nascoste);
					}),
				);
			}),
			switchMap((voci: SidebarMenu[]) => this.senzaDoppioniRfi(voci, cmp_id)),
			finalize(() => this.spinnerService.hide()),
			shareReplay(1),
			// in caso di errore svuota la cache, così il prossimo chiamante ritenta la chiamata
			tap({
				error: () => {
					if (this.menuItemsCache && this.menuItemsCache.key === key) {
						this.menuItemsCache = null;
					}
				},
			}),
		);
		this.menuItemsCache = { key, obs, store$ };
		return obs;
	}

	/** Chiavi delle sezioni a schede attive per l'azienda (cmp_menu_org.hubs), dalla stessa lettura del menu. */
	getMenuHubs(prt_id: number, cmp_id: number): Observable<string[]> {
		this.getMenuItems(prt_id, cmp_id);
		return this.menuItemsCache!.store$.pipe(map((store) => store?.hubs || []));
	}

	/** Come si aprono i gruppi del menu in alto (cmp_menu_org.stileSottomenu), dalla stessa lettura del menu. */
	getMenuStile(prt_id: number, cmp_id: number): Observable<MenuStileSottomenu> {
		this.getMenuItems(prt_id, cmp_id);
		return this.menuItemsCache!.store$.pipe(map((store) => store?.stileSottomenu || 'tendina'));
	}

	/** Menu in alto: voci a capo invece del dropdown "Altro" (cmp_menu_org.menuACapo). */
	getMenuACapo(prt_id: number, cmp_id: number): Observable<boolean> {
		this.getMenuItems(prt_id, cmp_id);
		return this.menuItemsCache!.store$.pipe(map((store) => !!store?.menuACapo));
	}

	/** Seconda barra come Word: voci a capo invece dello scorrimento orizzontale (cmp_menu_org.barraACapo). */
	getMenuBarraACapo(prt_id: number, cmp_id: number): Observable<boolean> {
		this.getMenuItems(prt_id, cmp_id);
		return this.menuItemsCache!.store$.pipe(map((store) => !!store?.barraACapo));
	}

	/** Seconda barra come Word: sottogruppi a blocchi col nome sotto invece della tendina in hover (cmp_menu_org.barraSottogruppi). */
	getMenuBarraBlocchi(prt_id: number, cmp_id: number): Observable<boolean> {
		this.getMenuItems(prt_id, cmp_id);
		return this.menuItemsCache!.store$.pipe(map((store) => store?.barraSottogruppi === 'blocchi'));
	}

	/**
	 * Con l'HelpDesk RFI attivo, Contratti e Certificati hanno una pagina unica (righe del cantiere + RFI): le voci RFI
	 * omonime si tolgono dal menu quando l'utente vede anche la voce unica. Le pagine RFI restano raggiungibili
	 * ("Apri in RFI" dalla pagina unica; la guardia ammette /ticketsrfi per le altre voci RFI del menu).
	 */
	private static readonly DOPPIONI_RFI: [string, string][] = [
		['gestione-contratti', 'ticketsrfi/gestione_contratti'],
		['cert-payment', 'ticketsrfi/certificati_pagamento'],
	];

	private senzaDoppioniRfi(menu: SidebarMenu[], cmp_id: number): Observable<SidebarMenu[]> {
		const url = (m: SidebarMenu) => (m?.url || '').trim().replace(/^\/+/, '');
		const presenti = new Set<string>();
		const raccogli = (voci: SidebarMenu[]) => (voci || []).forEach((m) => (presenti.add(url(m)), raccogli(m?.submenu || [])));
		raccogli(menu);
		const doppioni = new Set(PortalService.DOPPIONI_RFI.filter(([unica, rfi]) => presenti.has(unica) && presenti.has(rfi)).map(([, rfi]) => rfi));
		if (!doppioni.size) return of(menu);
		const togli = (voci: SidebarMenu[]): SidebarMenu[] =>
			(voci || [])
				.filter((m) => !doppioni.has(url(m)))
				.map((m) => (m.submenu?.length ? { ...m, submenu: togli(m.submenu) } : m))
				// gruppo rimasto senza voci e senza pagina propria: non si mostra
				.filter((m) => !!url(m) || !!m.submenu?.length);
		return this.rfiMode.attivo(cmp_id).pipe(map((rfi) => (rfi ? togli(menu) : menu)));
	}

	// Menu "grezzo" (senza cache né riorganizzazione): usato solo dall'editor di
	// organizzazione menu in anagrafica azienda, per non sporcare la cache single-slot
	getMenuItemsRaw(prt_id: number, cmp_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}menu/sidebarmenu/${prt_id}/${cmp_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	/** Ruoli dell'azienda e loro funzioni modulo, per l'anteprima "Vedi come" dell'editor del menu (solo ADMIN). */
	getMenuRolePreview(cmp_id: number): Observable<{ roles: { rl_id: number; rl_name: string }[]; functions: any[] }> {
		return this.http.get<any>(`${this.configService.portalUrl}menu/manager/role-preview/${cmp_id}`);
	}

	getManagerMenuItems(prt_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}menu/manager/sidebarmenu/${prt_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	checkAdmin(): Observable<boolean> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}menu/check-admin`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body as boolean),
				finalize(() => this.spinnerService.hide()),
			);
	}

	addMenuItem(menu: SidebarMenu): Observable<void> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}menu/manager/sidebarmenu`, menu, {
				observe: 'response',
			})
			.pipe(
				map(() => undefined),
				finalize(() => this.spinnerService.hide()),
			);
	}

	updateMenuItem(pg_id: number, menu: SidebarMenu): Observable<void> {
		this.spinnerService.show();
		return this.http
			.put(`${this.configService.portalUrl}menu/manager/sidebarmenu/${pg_id}`, menu, {
				observe: 'response',
			})
			.pipe(
				map(() => undefined),
				finalize(() => this.spinnerService.hide()),
			);
	}

	deleteMenuItem(pg_id: number, cascade: boolean = false): Observable<void> {
		this.spinnerService.show();
		return this.http
			.delete(`${this.configService.portalUrl}menu/manager/sidebarmenu/${pg_id}${cascade ? '?cascade=true' : ''}`, {
				observe: 'response',
			})
			.pipe(
				map(() => undefined),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// Posizioni menu: blocco e riordino alfabetico
	getMenuPositionLocks(): Observable<any> {
		return this.http.get(`${this.configService.portalUrl}menu/manager/locks`, { observe: 'response' }).pipe(map((response) => response.body));
	}

	updatePagePosition(pg_id: number, posizione: number, locked: boolean): Observable<void> {
		this.spinnerService.show();
		return this.http.put(`${this.configService.portalUrl}menu/manager/page-position`, { pg_id, posizione, locked }, { observe: 'response' }).pipe(
			map(() => undefined),
			finalize(() => this.spinnerService.hide()),
		);
	}

	// Applica le posizioni calcolate dal client (riordino per nome tradotto)
	applyMenuPositions(items: { pg_id: number; posizione: number }[]): Observable<void> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.portalUrl}menu/manager/positions`, items, { observe: 'response' }).pipe(
			map(() => undefined),
			finalize(() => this.spinnerService.hide()),
		);
	}

	// Export / Import menu (migrazione tra ambienti)
	exportMenuConfig(): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}menu/manager/export`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	previewMergeMenu(items: any[]): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.portalUrl}menu/manager/preview-merge`, items, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	importOverwriteMenu(items: any[]): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.portalUrl}menu/manager/import-overwrite`, items, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	importMergeMenu(items: any[]): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.portalUrl}menu/manager/import-merge`, items, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	// Funzioni
	sendPwMail(username: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}sendpwmail/${username}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getLabels(key: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}labels/get/${key}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getFunzioniByUser(modNome: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}funzioni/byuser/${modNome}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// Preferenze notifica dell'utente corrente (solo i tipi pertinenti ai suoi ruoli nell'azienda corrente)
	getMyMailSettings(cmpId: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}mail-settings/my?cmp_id=${cmpId || 0}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	saveMyMailSettings(items: any[]): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}mail-settings/my`, items, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	AssEmployeeUser(tmpEmp: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}user/assempl`, tmpEmp, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}
	AssDoctorUser(tmpdoctor: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}user/assdoctor`, tmpdoctor, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	AssCollaboratorUser(tmpCol: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}user/asscol`, tmpCol, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// Crea l'utenza per il collaboratore di un CLIENTE: ritorna usr_id (>0), -1 = email già registrata, -2 = nessun portale per l'azienda
	AssCustomerCollaboratorUser(tmpCol: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}user/asscust`, tmpCol, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// usr_id dell'utenza esistente per email/username (0 se non esiste)
	GetUserIdByUsername(username: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}user/getbyusername?username=${encodeURIComponent(username)}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// Aggancia un collaboratore a un'utenza portale esistente (email duplicata confermata dall'amministratore)
	LinkCollaboratorExistingUser(usr_id: number, col_id: number, cmp_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}user/linkcol?usr_id=${usr_id}&col_id=${col_id}&cmp_id=${cmp_id}`, null, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	SaveAssRoleFunc(funzioni: any[]): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}funzioni/saveassrlfu`, funzioni, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getRoleAssignmentsByModule(modNome: string): Observable<any> {
		return this.http
			.get(`${this.configService.portalUrl}funzioni/roleassignments/bymodule/${modNome}`, {
				observe: 'response',
			})
			.pipe(map((response) => response.body));
	}

	getFunzioni(modNome: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}funzioni/funzionibymodnome/${modNome}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getRuoli(tipo: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}funzioni/getruoli/${tipo}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	addPortale(addportale: Portal): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}portal/add`, addportale, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getPortali(cerca: string, cercaOption: string, orderby: string, limitda: number, limitper: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}portal/list?${cercaOption}=${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getServicePresets(): Observable<any> {
		this.spinnerService.show();
		const companyId = this.configService.getCompany_id();
		return this.http.get(`${this.configService.companiesUrl}company_service/service-presets/company/${companyId}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getAlias(al_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}alias/get/${al_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	addAlias(addalias: Alias): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}alias/add`, addalias, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	GetListAlias(pt_id: number, cerca: string, orderby: string, limitda: number, limitper: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}alias/list/${pt_id}?al_descrizione=${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getAliasCompanies(al_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}alias/getcompany/${al_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	SaveAssAliasAzienda(al_id: number, cmp_id: number, cmp_isChecked: boolean): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}alias/setcompany/${al_id}/${cmp_id}/${cmp_isChecked}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	deleteAlias(al_id: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.delete(`${this.configService.portalUrl}alias/del/${al_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	GetListPortali(cerca: string, orderby: string, limitda: number, limitper: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}portal/list?pt_descrizione=${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getPortale(pt_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}portal/get/${pt_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	setPortale(pt_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}portal/set/${pt_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				tap(() => this.clearPortalScopedFilters()),
				finalize(() => this.spinnerService.hide()),
			);
	}

	/**
	 * Al cambio portale i filtri che le liste RFI (tickets, sopralluoghi, stazioni, contratti,
	 * certificati) salvano in sessionStorage appartengono al portale precedente: reparto, pagina
	 * e ticket selezionato di un altro portale producevano liste vuote o incoerenti dopo il reload.
	 * Le chiavi sono quelle di TicketsService, non iniettato qui per non creare dipendenze cicliche.
	 */
	private clearPortalScopedFilters(): void {
		const keys = ['SessionKeyTickets', 'SessionKeySopralluoghi', 'SessionKeyStazioni', 'SessionKeyFornitori', 'SessionKeyTipiIntervento', 'SessionKeyTipiDocumento', 'SessionKeyContratti', 'SessionKeyGestioneContratti', 'SessionKeyCertificatiPagamento', 'tickets_sel'];
		try {
			keys.forEach((k) => sessionStorage.removeItem(k));
		} catch {
			// sessionStorage non disponibile: nessun filtro da azzerare
		}
	}

	// health: '' | issues | ok | app — stato di accesso calcolato dal backend (users_list_get)
	GetListUtenti(
		cerca: string,
		orderby: string,
		limitda: number,
		limitper: number,
		cmp_id: number,
		role_id: number = 0,
		usr_type: string = '',
		pt_id_filter: number = 0,
		health: string = '',
		cmp_id_filter: number = 0,
		cmp_rl_id: number = 0,
		name_mismatch: boolean = false,
	): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(
				`${this.configService.portalUrl}user/list?usr_username=${encodeURIComponent(cerca || '')}&orderby=${encodeURIComponent(orderby || '')}&limitda=${limitda}&limitper=${limitper}&cmp_id=${cmp_id}&role_id=${role_id}&usr_type=${usr_type}&pt_id_filter=${pt_id_filter}&health=${health}&cmp_id_filter=${cmp_id_filter}&cmp_rl_id=${cmp_rl_id}&name_mismatch=${name_mismatch}`,
				{ observe: 'response' },
			)
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// Ripara i prerequisiti di accesso dell'utente (identita' di login, mail, dispositivo, portale, ruolo).
	// pt_id: portale da assegnare se l'utente non ne ha. Ritorna i codici delle operazioni eseguite.
	repairUserAccess(usr_id: number, pt_id: number = 0): Observable<string[]> {
		this.spinnerService.show();
		return this.http.post<string[]>(`${this.configService.portalUrl}user/repair/${usr_id}?pt_id=${pt_id || 0}`, null, { observe: 'response' }).pipe(
			map((response) => response.body ?? []),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetListRuoli(cerca: string, orderby: string, limitda: number, limitper: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}ruoli/lista?${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getRuoliUser(usr_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}ruoli/listaruoliuser/${usr_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	GetListRuoliPagina(pg_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}ruoli/listaruolipagina/${pg_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	GetListPortaliPagina(pg_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}portal/listaportalipagina/${pg_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	GetListPagine(cerca: string, orderby: string, limitda: number, limitper: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}pagine?${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetListSottopagine(cerca: string, orderby: string, limitda: number, limitper: number, parent_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}pagine/list?${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}&parent_id=${parent_id}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getUser(usr_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}user/get/${usr_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}
	// Aggiorna l'immagine profilo dell'utente autenticato (data-URL base64, o '' per rimuoverla).
	updateAvatar(avatar: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.put(
				`${this.configService.portalUrl}user/avatar`,
				{ avatar },
				{
					observe: 'response',
				},
			)
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	loginAsUser(usr_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}user/loginAsUser/${usr_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getUsersByRole(cerca: string, orderby: string, limitda: number, limitper: number, role_id: number, cmp_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http.get(`${this.configService.portalUrl}user/list?usr_username=${cerca}&orderby=${orderby}&limitda=${limitda}&limitper=${limitper}&role_id=${role_id}&cmp_id=${cmp_id}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	// pt_id serve solo in creazione: e' il portale a cui agganciare la nuova utenza
	addUtente(addutente: UserInfo, pt_id: number = 0): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}user/add?pt_id=${pt_id}`, addutente, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	delUtente(usr_id: string): Observable<any> {
		this.spinnerService.show();
		return this.http
			.delete(`${this.configService.portalUrl}user/del/${usr_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	delRuoloPagina(pg_id: number, rl_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http.delete(`${this.configService.portalUrl}ruoli/deleteruolopagina?pg_id=${pg_id}&rl_id=${rl_id}`, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	delPortalePagina(pg_id: number, pt_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http.delete(`${this.configService.portalUrl}portal/deleteportalepagina?pg_id=${pg_id}&pt_id=${pt_id}`, { observe: 'response' }).pipe(
			map((response) => {
				console.log('pg_id = ', pg_id, 'pt_id = ', pt_id);
				return response.body;
			}),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getPortaliUser(user_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}portal/utente/${user_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	SaveAssRuoli(user_id: number, ruoli: Role): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}ruoli/saveassruoliuser/${user_id}`, ruoli, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	SaveAssPortali(user_id: number, portale: Portal[]): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}portal/saveassportali/${user_id}`, portale, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	SaveAsRuoloPagina(pg_id: number, ruolo: Role): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}ruoli/saveasruolopagina/${pg_id}`, ruolo, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	SaveAsPortalePagina(pg_id: number, portale: Portal): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.portalUrl}portal/saveasportalepagina/${pg_id}`, portale, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	login(data: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.serviceUrl_Login}login/1`, data, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	login_check_app(data: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.serviceUrl_Login}login/check_app/1`, data, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	send_sms(data: any, checkUserRegistered: boolean, registrationStep: any): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}send_sms/${checkUserRegistered}${registrationStep ? '?rs=' + registrationStep : ''}`, data, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/**
	 * number/token/remember_me nel body: in query string il codice OTP finiva nei log di
	 * accesso di OpenLiteSpeed, nei reverse proxy e negli APM. Il backend accetta ancora
	 * i query param come fallback, quindi va pubblicato PRIMA di questa versione.
	 *
	 * token e' una stringa e non un number: i codici che iniziano per zero (circa uno su
	 * dieci) perdevano lo zero nella conversione e non venivano mai accettati.
	 */
	check_token_sms(data: any, number: string, token: string, keepMeSignedIn: boolean): Observable<any> {
		this.spinnerService.show();
		const { protocol, host } = window.location;
		let baseUrl = `${protocol}//${host}`;
		data.url = baseUrl;
		return this.http.post(`${this.configService.serviceUrl_Login}check_token_sms`, { ...data, number, token, remember_me: keepMeSignedIn }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	save_nominativo(firstname: string, lastname: string, displayname: string, phone: number, email: string, password: string, codicefiscale: string, userData: any, guid: string, type: string): Observable<any> {
		this.spinnerService.show();
		let user = Object.assign({}, userData);
		user.usr_firstname = firstname;
		user.usr_lastname = lastname;
		user.usr_displayname = displayname;
		user.usr_phone = phone.toString();
		user.usr_mail = email;
		user.usr_password = password;
		user.usr_codicefiscale = codicefiscale;
		user.guid_notifica = guid;
		user.device_type = type;

		return this.http
			.put(`${this.configService.serviceUrl_Login}save_nominativo`, user, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	setUserLastAccess(): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.serviceUrl_Login}set_user_last_access`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	ViewHideBtn(idElement: string, view: boolean): void {
		const btn = document.getElementById(idElement);
		if (btn) {
			if (view) {
				btn.classList.remove('hidden');
			} else {
				btn.classList.add('hidden');
			}
		}
	}

	IsSuperUser(): boolean {
		let res = false;
		let currentUser: UserInfo;
		currentUser = this.configService.getLocalStorageObjectJson('currentUser');
		if (currentUser != null && ConfigService.hasAuthority(currentUser.authorities, 'ADMIN')) {
			res = true;
		}
		return res;
	}

	SetOrderTypeLabels(labels: Label[], OrderBy: string): void {
		if (OrderBy !== undefined && OrderBy.length > 0) {
			for (let i = 0; i < labels.length; i++) {
				if (labels[i].lb_fieldname === OrderBy.split(' ')[0]) {
					let TypeOrder = 'ASC';
					if (OrderBy.split(' ')[1] === 'ASC') {
						TypeOrder = 'DESC';
					}
					labels[i].sort = '-';
					labels[i].lb_orderby = TypeOrder;
					break;
				}
			}
		}
	}

	//TODO
	/*HavePermission(funzioni: Funzioni[], namePermission: string): boolean {
		let res = false;
		for (let i = 0; i < funzioni.length; i++) {
			if (funzioni[i].mdfu_nome === namePermission) {
				res = true;
				break;
			}
		}
		return res;
	}*/

	CleanOrderByLabels(labels: Label[], labelSort: string): void {
		for (let i = 0; i < labels.length; i++) {
			if (labels[i].lb_fieldname === labelSort) {
				continue;
			}
			labels[i].lb_orderby = '';
		}
	}

	logout() {
		localStorage.clear();
	}

	updateRuolo(ruolo: Role): Observable<any> {
		return this.http
			.put(`${this.configService.portalUrl}ruoli/update`, ruolo, {
				observe: 'response',
			})
			.pipe(map((response) => response.body));
	}

	updatePagina(pagina: Page): Observable<any> {
		return this.http
			.put(`${this.configService.portalUrl}pagine/update`, pagina, {
				observe: 'response',
			})
			.pipe(map((response) => response.body));
	}

	addRuolo(ruolo: Role): Observable<any> {
		return this.http
			.post(`${this.configService.portalUrl}ruoli/add`, ruolo, {
				observe: 'response',
			})
			.pipe(map((response) => response.body));
	}

	addPagina(pagina: Page): Observable<any> {
		return this.http
			.post(`${this.configService.portalUrl}pagine/add`, pagina, {
				observe: 'response',
			})
			.pipe(map((response) => response.body));
	}

	getRuolo(rl_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}ruoli/ruolobyid/${rl_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getPagina(pg_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}pagine/paginabyid/${pg_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getRuoloAssociazioni(rl_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}ruoli/associazioni/${rl_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	delRuolo(rl_id: number, force: boolean = false): Observable<any> {
		this.spinnerService.show();
		return this.http
			.delete(`${this.configService.portalUrl}ruoli/del/${rl_id}?force=${force}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	delPagina(rl_id: number): Observable<any> {
		this.spinnerService.show();
		return this.http
			.delete(`${this.configService.portalUrl}pagine/del/${rl_id}`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	getComuni(): Observable<any> {
		this.spinnerService.show();
		return this.http
			.get(`${this.configService.portalUrl}combo/comuni`, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	// Richiesta di eliminazione account e dati associati (requisito Google Play).
	// L'identità è ricavata dal token lato backend; qui inviamo solo motivo e nominativo per il testo dell'email.
	requestAccountDeletion(payload: { reason: string; displayname: string }): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.serviceUrl_Login}request_account_deletion`, payload, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	sendMailresetPasswordUser(user: any): Observable<any> {
		this.spinnerService.show();
		return this.http
			.post(`${this.configService.serviceUrl_Login}send_mail_reset_user_password`, user, {
				observe: 'response',
			})
			.pipe(
				map((response) => response.body),
				finalize(() => this.spinnerService.hide()),
			);
	}

	register_user(usr_id: number, username: string, firstname: string, lastname: string, displayname: string, phone: string | null, email: string, password: string, codicefiscale: string, guid: string, type: string, sessionRegisterUUID: string): Observable<any> {
		this.spinnerService.show();
		let user = Object.assign({});
		user.usr_id = usr_id;
		user.usr_username = username;
		user.usr_firstname = firstname;
		user.usr_lastname = lastname;
		user.usr_displayname = displayname;
		// Telefono in E.164: e' la forma con cui il backend ritrova la riga users_bid creata
		// in fase di verifica OTP e la collega all'utente
		user.usr_phone = phone ? phone : null;
		user.usr_mail = email;
		user.usr_password = password;
		user.usr_codicefiscale = codicefiscale;
		user.guid_notifica = guid;
		user.device_type = type;

		// L'indirizzo del sito identifica il portale a cui associare il nuovo utente (prt_portal_alias)
		const { protocol, host } = window.location;
		const baseUrl = `${protocol}//${host}`;

		return this.http.put(`${this.configService.serviceUrl_Login}register_user`, { user: user, guid: sessionRegisterUUID, url: baseUrl }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/**
	 * number/token/guid nel body: prima numero e codice stavano nel path, che i web server
	 * registrano sempre nell'access log. Il backend tiene la vecchia rotta come fallback,
	 * quindi va pubblicato PRIMA di questa versione.
	 */
	check_token_registration(number: string, token: string, guid: string): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}check_token_registration`, { number, token, guid }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	send_token_registration(data: any, registrationStep: number, guid: string): Observable<any> {
		this.spinnerService.show();
		// Niente slash finale: da Spring 6 il match con lo slash in coda non e' piu' automatico
		return this.http.post(`${this.configService.serviceUrl_Login}send_token_registration${registrationStep ? '?rs=' + registrationStep : ''}`, { user: data, guid: guid }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/**
	 * Accesso con un provider di identita'. Il token viene verificato lato server: qui
	 * viaggia solo quello che l'SDK del provider ha restituito.
	 */
	login_social(provider: string, token: string, firstName = '', lastName = ''): Observable<any> {
		this.spinnerService.show();
		// L'indirizzo serve se l'accesso porta a creare una nuova utenza: identifica il
		// portale a cui associarla (prt_portal_alias). Nome e cognome servono solo ad Apple,
		// che li fornisce una volta sola al primo accesso e non li mette nel token
		const { protocol, host } = window.location;
		const baseUrl = `${protocol}//${host}`;

		return this.http.post(`${this.configService.serviceUrl_Login}login_social`, { provider: provider, token: token, url: baseUrl, firstName: firstName, lastName: lastName }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/** Crea l'utenza dopo che l'utente ha confermato l'anagrafica proposta dal provider. */
	register_social(provider: string, token: string, firstName: string, lastName: string, displayName: string): Observable<any> {
		this.spinnerService.show();
		const { protocol, host } = window.location;
		const baseUrl = `${protocol}//${host}`;

		return this.http.post(`${this.configService.serviceUrl_Login}register_social`, { provider: provider, token: token, url: baseUrl, firstName: firstName, lastName: lastName, displayName: displayName }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/** Salva l'anagrafica dal Profilo. L'utente e' quello del token, non viene passato. */
	upd_user_nominativo(firstName: string, lastName: string, displayName: string): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}upd_user_nominativo`, { firstName: firstName, lastName: lastName, displayName: displayName }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/**
	 * Primo accesso con un provider su un'utenza esistente: manda il codice al recapito
	 * scelto. Il token del provider viene rimandato perche' il backend lo riverifica a ogni
	 * passo, invece di tenere lo stato del collegamento in sessione.
	 */
	send_token_social_link(provider: string, token: string, channel: number): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}send_token_social_link`, { provider: provider, token: token, channel: channel }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/** Conferma il codice e collega l'identita' del provider all'utenza. */
	check_token_social_link(provider: string, token: string, channel: number, otp: string): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}check_token_social_link`, { provider: provider, token: token, channel: channel, otp: otp }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/** Manda l'OTP a un numero che l'utente loggato vuole aggiungere alla propria utenza. */
	send_token_phone(phone: string, guid: string): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}send_token_phone`, { phone: phone, guid: guid }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	/** Verifica l'OTP e collega il numero all'utenza: da qui in poi e' anche un identificativo di accesso. */
	check_token_phone(phone: string, token: string, guid: string): Observable<any> {
		this.spinnerService.show();
		return this.http.post(`${this.configService.serviceUrl_Login}check_token_phone`, { phone: phone, token: token, guid: guid }, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getUtentebyUuid(uuid: string): Observable<any> {
		this.spinnerService.show();
		return this.http.get(this.configService.serviceUrl_Login + 'get_user_info_by_uuid/' + uuid, { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	updPswd(usr_id: number, uuid: string, pswd: string): Observable<any> {
		return this.http.post(this.configService.serviceUrl_Login + 'upd_user_password/' + usr_id, [uuid, pswd], { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	getPortalLanguages(): Observable<any> {
		this.spinnerService.show();
		return this.http.get(this.configService.portalUrl + 'portal/list_languages', { observe: 'response' }).pipe(
			map((response) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}
}
